import { useState, useMemo, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { useConfiguration } from "../../../components/ConfigurationContext";
import { useBranch } from "../../../components/BranchContext";
import { accountingService } from "../../../../../services/accounting";
import {
  IListVouchersRequest,
  IVoucherListItem,
  IVoucherDetail,
  ICreateDraftVoucherRequest,
  IPostVoucherRequest,
  IReverseVoucherRequest,
  IGetVoucherRequest,
  IUpdateDraftVoucherRequest,
  IDeleteVoucherRequest,
  IVoucherInput,
} from "../../../accounting.interface";
import moment from "moment";
import { toLower } from "lodash";

// Updated interfaces to match API responses
export interface VoucherFilters {
  dateFrom?: string;
  dateTo?: string;
  selectedBranch?: string;
  voucherType?: "payment" | "receipt" | "journal" | "all";
  status?: "draft" | "posted" | "reversed" | "all";
  paymentMethod?: string;
  beneficiary?: string;
  referenceNumber?: string;
  narrationContains?: string;
}

export interface VoucherTableData extends IVoucherListItem {
  // Add any computed fields if needed
}

export interface ConfirmationDialogState {
  open: boolean;
  type: "delete" | "reverse" | "post" | null;
  voucher: IVoucherListItem | null;
}

export interface VoucherFormData {
  voucherType: "payment" | "receipt" | "journal";
  voucherDate: string;
  narration: string;
  beneficiary?: string;
  linkedEntity?: string;
  paymentMethod?: "cash" | "bank_transfer" | "cheque" | "card" | "online";
  referenceNumber?: string;
  branchId?: string;
  voucherLines: Array<{
    account_id: string;
    account_code?: string;
    account_name?: string;
    debit: number;
    credit: number;
    description?: string;
  }>;
}

export interface UseVouchersReturn {
  // State
  filters: VoucherFilters;
  selectedVoucher: IVoucherDetail | null;
  isDetailDialogOpen: boolean;
  isFormDialogOpen: boolean;
  isLoading: boolean;
  tableData: VoucherTableData[];
  confirmationDialog: ConfirmationDialogState;
  page: number;
  rowsPerPage: number;
  filteredVouchers: IVoucherListItem[];
  totalCount: number;

  // Handlers
  handleFiltersChange: (newFilters: Partial<VoucherFilters>) => void;
  handleResetFilters: () => void;
  handleApplyFilters: () => void;
  handleVoucherClick: (voucher: IVoucherListItem) => void;
  handleCloseDetailDialog: () => void;
  handleOpenFormDialog: () => void;
  handleCloseFormDialog: () => void;
  handleEditVoucher: (voucher: IVoucherListItem) => void;
  handleReverseVoucher: (voucher: IVoucherListItem) => void;
  handleDeleteVoucher: (voucher: IVoucherListItem) => void;
  handlePostVoucher: (voucher: IVoucherListItem) => void;
  handleSubmitVoucher: (voucherData: VoucherFormData) => Promise<void>;
  handleCopyVoucherId: (voucherId: string) => void;
  handleDownloadPDF: (voucher: IVoucherListItem) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  handleCloseConfirmationDialog: () => void;
  handleConfirmAction: () => Promise<void>;
  refreshData: () => Promise<void>;
}

const createDefaultFilters = (): VoucherFilters => ({
  dateFrom: undefined,
  dateTo: undefined,
  selectedBranch: "organization",
  voucherType: "all",
  status: "all",
  paymentMethod: undefined,
  beneficiary: undefined,
  referenceNumber: undefined,
  narrationContains: undefined,
});

export const useVouchers = (): UseVouchersReturn => {
  const { voucherSettings } = useConfiguration();
  const { selectedBranch } = useBranch();

  // State management
  const [vouchers, setVouchers] = useState<IVoucherListItem[]>([]);
  const [filters, setFilters] = useState<VoucherFilters>(
    createDefaultFilters()
  );
  const [selectedVoucher, setSelectedVoucher] = useState<IVoucherDetail | null>(
    null
  );
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState<IVoucherListItem | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmationDialog, setConfirmationDialog] =
    useState<ConfirmationDialogState>({
      open: false,
      type: null,
      voucher: null,
    });

  // Fetch vouchers from API
  const fetchVouchers = useCallback(async () => {
    setIsLoading(true);
    try {
      const requestData: IListVouchersRequest = {
        pageIndex: page - 1, // API uses 0-based indexing
        pageSize: rowsPerPage,
        sortField: "created_at",
        sortOrder: "desc",
        selected_branch_id:
          filters.selectedBranch === "organization"
            ? undefined
            : filters.selectedBranch,
        status: filters.status === "all" ? undefined : toLower(filters.status),
        voucher_type:
          filters.voucherType === "all" ? undefined : filters.voucherType,
        searchQuery: filters.narrationContains,
        startDate: filters.dateFrom
          ? moment(filters.dateFrom).valueOf()
          : undefined,
        endDate: filters.dateTo ? moment(filters.dateTo).valueOf() : undefined,
      };

      // Add additional filters if provided
      const additionalFilters = [];
      if (filters.paymentMethod) {
        additionalFilters.push({
          field: "payment_method",
          operator: "equals",
          value: filters.paymentMethod,
        });
      }
      if (filters.beneficiary) {
        additionalFilters.push({
          field: "beneficiary",
          operator: "contains",
          value: filters.beneficiary,
        });
      }
      if (filters.referenceNumber) {
        additionalFilters.push({
          field: "reference_number",
          operator: "contains",
          value: filters.referenceNumber,
        });
      }

      if (additionalFilters.length > 0) {
        requestData.filters = additionalFilters;
      }

      const response = await accountingService.listVouchers(requestData);
      setVouchers(response.data);
      setTotalCount(response.data.length); // Note: API doesn't return total count yet
    } catch (error: any) {
      console.error("Failed to fetch vouchers:", error);
      toast.error(error.message || "Failed to fetch vouchers");
      setVouchers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  // Fetch voucher details
  const fetchVoucherDetails = useCallback(async (voucherId: string) => {
    try {
      const requestData: IGetVoucherRequest = { voucher_id: voucherId };
      const response = await accountingService.getVoucher(requestData);
      setSelectedVoucher(response.data);
    } catch (error: any) {
      console.error("Failed to fetch voucher details:", error);
      toast.error(error.message || "Failed to fetch voucher details");
    }
  }, []);

  // Effect to fetch data when dependencies change
  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  // Computed values
  const filteredVouchers = useMemo(() => {
    return vouchers; // API already applies filters
  }, [vouchers]);

  const tableData = useMemo(() => {
    return vouchers.map((voucher) => ({
      ...voucher,
      // Add any computed fields if needed
    }));
  }, [vouchers]);

  // Filter handlers
  const handleFiltersChange = useCallback(
    (newFilters: Partial<VoucherFilters>) => {
      setFilters((prev) => ({ ...prev, ...newFilters }));
      setPage(1); // Reset to first page when filters change
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    setFilters(createDefaultFilters());
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    // Filters are applied automatically via fetchVouchers
    fetchVouchers();
  }, [fetchVouchers]);

  // Dialog handlers
  const handleVoucherClick = useCallback(
    async (voucher: IVoucherListItem) => {
      await fetchVoucherDetails(voucher.voucher_id);
      setIsDetailDialogOpen(true);
    },
    [fetchVoucherDetails]
  );

  const handleCloseDetailDialog = useCallback(() => {
    setIsDetailDialogOpen(false);
    setSelectedVoucher(null);
  }, []);

  const handleOpenFormDialog = useCallback(() => {
    setEditingVoucher(null);
    setIsFormDialogOpen(true);
  }, []);

  const handleCloseFormDialog = useCallback(() => {
    setIsFormDialogOpen(false);
    setEditingVoucher(null);
  }, []);

  const handleEditVoucher = useCallback((voucher: IVoucherListItem) => {
    if (voucher.status !== "draft") {
      toast.error("Only draft vouchers can be edited");
      return;
    }
    setEditingVoucher(voucher);
    setIsFormDialogOpen(true);
    setIsDetailDialogOpen(false);
  }, []);

  // Action handlers
  const handleReverseVoucher = useCallback((voucher: IVoucherListItem) => {
    if (voucher.status !== "posted") {
      toast.error("Only posted vouchers can be reversed");
      return;
    }
    setConfirmationDialog({
      open: true,
      type: "reverse",
      voucher,
    });
  }, []);

  const handleDeleteVoucher = useCallback(
    (voucher: IVoucherListItem) => {
      if (voucher.status !== "draft") {
        toast.error("Only draft vouchers can be deleted");
        return;
      }

      // Check if draft mode is enabled in configuration
      if (!voucherSettings?.field_requirements?.enable_voucher_draft_mode) {
        toast.error("Draft vouchers are disabled in configuration");
        return;
      }

      setConfirmationDialog({
        open: true,
        type: "delete",
        voucher,
      });
    },
    [voucherSettings?.field_requirements?.enable_voucher_draft_mode]
  );

  const handlePostVoucher = useCallback((voucher: IVoucherListItem) => {
    if (voucher.status !== "draft") {
      toast.error("Only draft vouchers can be posted");
      return;
    }
    setConfirmationDialog({
      open: true,
      type: "post",
      voucher,
    });
  }, []);

  const handleSubmitVoucher = useCallback(
    async (voucherData: VoucherFormData) => {
      setIsLoading(true);
      try {
        if (editingVoucher) {
          // Update existing voucher
          const updateData: IUpdateDraftVoucherRequest = {
            voucher_id: editingVoucher.voucher_id,
            voucher: {
              voucher_type: voucherData.voucherType,
              voucher_date: moment(voucherData.voucherDate).valueOf(),
              narration: voucherData.narration,
              beneficiary: voucherData.beneficiary,
              linked_entity: voucherData.linkedEntity,
              payment_method: voucherData.paymentMethod,
              reference_number: voucherData.referenceNumber,
              branch_id: voucherData.branchId,
              voucher_lines: voucherData.voucherLines,
            },
          };

          await accountingService.updateDraftVoucher(updateData);
          toast.success("Voucher updated successfully");
        } else {
          // Create new voucher
          const voucherInput: IVoucherInput = {
            voucher_type: voucherData.voucherType,
            voucher_date: moment(voucherData.voucherDate).valueOf(),
            narration: voucherData.narration,
            beneficiary: voucherData.beneficiary,
            linked_entity: voucherData.linkedEntity,
            payment_method: voucherData.paymentMethod,
            reference_number: voucherData.referenceNumber,
            branch_id: voucherData.branchId,
            voucher_lines: voucherData.voucherLines,
          };

          const createData: ICreateDraftVoucherRequest = {
            voucher: voucherInput,
          };
          await accountingService.createDraftVoucher(createData);
          toast.success("Voucher created successfully");
        }

        // Refresh data
        await fetchVouchers();
        setIsFormDialogOpen(false);
        setEditingVoucher(null);
      } catch (error: any) {
        console.error("Failed to save voucher:", error);
        toast.error(
          error.message || "Failed to save voucher. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [editingVoucher, fetchVouchers]
  );

  const handleCopyVoucherId = useCallback((voucherId: string) => {
    navigator.clipboard.writeText(voucherId);
    toast.success("Voucher ID copied to clipboard");
  }, []);

  const handleDownloadPDF = useCallback((voucher: IVoucherListItem) => {
    // TODO: Implement PDF download functionality
    toast.info("PDF download functionality not yet implemented");
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setRowsPerPage(newPageSize);
    setPage(1); // Reset to first page
  }, []);

  const handleCloseConfirmationDialog = useCallback(() => {
    setConfirmationDialog({
      open: false,
      type: null,
      voucher: null,
    });
  }, []);

  const handleConfirmAction = useCallback(async () => {
    const { type, voucher } = confirmationDialog;
    if (!voucher || !type) return;

    setIsLoading(true);
    try {
      switch (type) {
        case "delete":
          const deleteData: IDeleteVoucherRequest = {
            voucher_id: voucher.voucher_id,
            deletion_reason: "User requested deletion",
          };
          await accountingService.deleteVoucher(deleteData);
          toast.success("Voucher deleted successfully");
          break;

        case "reverse":
          const reverseData: IReverseVoucherRequest = {
            voucher_id: voucher.voucher_id,
            reversal_reason: "User requested reversal",
          };
          await accountingService.reverseVoucher(reverseData);
          toast.success("Voucher reversed successfully");
          break;

        case "post":
          const postData: IPostVoucherRequest = {
            voucher_id: voucher.voucher_id,
          };
          await accountingService.postVoucher(postData);
          toast.success("Voucher posted successfully");
          break;
      }

      handleCloseConfirmationDialog();
      await fetchVouchers();
    } catch (error: any) {
      console.error(`Failed to ${type} voucher:`, error);
      toast.error(error.message || `Failed to ${type} voucher`);
    } finally {
      setIsLoading(false);
    }
  }, [confirmationDialog, fetchVouchers, handleCloseConfirmationDialog]);

  const refreshData = useCallback(async () => {
    await fetchVouchers();
  }, [fetchVouchers]);

  return {
    // State
    filters,
    selectedVoucher,
    isDetailDialogOpen,
    isFormDialogOpen,
    isLoading,
    tableData,
    confirmationDialog,
    page,
    rowsPerPage,
    filteredVouchers,
    totalCount,

    // Handlers
    handleFiltersChange,
    handleResetFilters,
    handleApplyFilters,
    handleVoucherClick,
    handleCloseDetailDialog,
    handleOpenFormDialog,
    handleCloseFormDialog,
    handleEditVoucher,
    handleReverseVoucher,
    handleDeleteVoucher,
    handlePostVoucher,
    handleSubmitVoucher,
    handleCopyVoucherId,
    handleDownloadPDF,
    handlePageChange,
    handlePageSizeChange,
    handleCloseConfirmationDialog,
    handleConfirmAction,
    refreshData,
  };
};
